import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import Calendar from './components/Calendar';
import ListView from './components/ListView';
import Filters from './components/Filters';
import CountryChart from './components/CountryChart';
import EventDetail from './components/EventDetail';
import MapView from './components/MapView';
import AboutModal from './components/AboutModal';
import SubListView from './components/SubListView';
import useLikes from './hooks/useLikes';
import { getScans, getOpportunities, getLocations, getDefaultScan, getOpportunitiesMulti } from './api/client';
import './App.css';

const API = process.env.REACT_APP_API_URL || '';

function safeFormat(dateStr, fmt) {
  if (!dateStr) return '—';
  try { return format(new Date(dateStr), fmt); }
  catch { return '—'; }
}

const SCAN_STATE_LABEL = {
  completed_successfully: '✓',
  zero_results:           '∅',
  stopped:                '■',
  aborted:                '✕',
  broke:                  '!',
  invalid_by_user:        '⊘',
  abort_search_errors:    '⚠',
  started:                '●',
};

const DEFAULT_API_FILTERS = {
  keyword: '', types: [], disciplines: [], countries: [],
};

const DEFAULT_CLIENT_FILTERS = {
  year: null, months: [],
  hideNoAddress: false, hideUndated: false, likedOnly: false,
};

export default function App() {
  const [view, setView]                     = useState('calendar');
  const [subView, setSubView]               = useState(null);
  const [aboutOpen, setAboutOpen]           = useState(false);
  const [appVersion, setAppVersion]         = useState('');
  const [scans, setScans]                   = useState([]);
  const [selectedScanIds, setSelectedScanIds] = useState([]);
  const [scanPickerOpen, setScanPickerOpen] = useState(false);
  const [opportunities, setOpportunities]   = useState([]);
  const [apiFilters, setApiFilters]         = useState(DEFAULT_API_FILTERS);
  const [clientFilters, setClientFilters]   = useState(DEFAULT_CLIENT_FILTERS);
  const [selectedEvent, setSelectedEvent]   = useState(null);
  const [loading, setLoading]               = useState(true);
  const [scansLoading, setScansLoading]     = useState(true);
  const [error, setError]                   = useState(null);

  const { likedIds, toggleLike } = useLikes();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Load version
  useEffect(() => {
    fetch('/VERSION').then(r => r.text()).then(v => setAppVersion(v.trim())).catch(() => {});
  }, []);

  // Close about popover on outside click
  useEffect(() => {
    if (!aboutOpen) return;
    const handler = (e) => {
      if (!e.target.closest('.about-popover-wrap') && !e.target.closest('.logo-clickable')) {
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aboutOpen]);

  // Load scans on mount
  useEffect(() => {
    setScansLoading(true);
    getScans()
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : [];
        setScans(data);
        return getDefaultScan().then(d => {
          setSelectedScanIds(d.data.scan_ids || []);
        }).catch(() => {
          const first = data.find(s => s.scan_state === 'completed_successfully');
          if (first) setSelectedScanIds([first.scan_id]);
        });
      })
      .catch(() => setScans([]))
      .finally(() => setScansLoading(false));
  }, []);

  // Fetch opportunities when scan or api filters change
  const fetchOpportunities = useCallback(async () => {
    if (!selectedScanIds.length) { setOpportunities([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const params = { scan_id: selectedScanIds[0] };
      if (apiFilters.keyword) params.keyword = apiFilters.keyword;
      if (apiFilters.types.length === 1) params.opportunity_type = apiFilters.types[0];
      if (apiFilters.disciplines.length === 1) params.opportunity_class = apiFilters.disciplines[0];
      if (apiFilters.countries.length === 1) params.country = apiFilters.countries[0];

      const r = await getOpportunities(params);
      let data = Array.isArray(r.data) ? r.data : [];

      if (apiFilters.types.length > 1)
        data = data.filter(o => apiFilters.types.includes(o.opportunity_type));
      if (apiFilters.disciplines.length > 1)
        data = data.filter(o => apiFilters.disciplines.includes(o.opportunity_class));
      if (apiFilters.countries.length > 1)
        data = data.filter(o => apiFilters.countries.includes(o.address_country));

      setOpportunities(data);
    } catch (e) {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [selectedScanIds, apiFilters]);

  useEffect(() => { fetchOpportunities(); }, [fetchOpportunities]);

  // Client-side filtering via useMemo — never triggers a re-fetch
  const safeOpps = useMemo(() => {
    let opps = Array.isArray(opportunities) ? opportunities : [];
    if (clientFilters.hideNoAddress)
      opps = opps.filter(o => o.address_city || o.address_state || o.address_raw);
    if (clientFilters.hideUndated)
      opps = opps.filter(o => o.event_start_date);
    if (clientFilters.likedOnly)
      opps = opps.filter(o => likedIds.has(Number(o.capture_id)));
    if (clientFilters.year || (clientFilters.months && clientFilters.months.length > 0)) {
      opps = opps.filter(o => {
        if (!o.event_start_date) return true;
        const d = new Date(o.event_start_date);
        const yearOk  = !clientFilters.year   || d.getFullYear() === clientFilters.year;
        const monthOk = !clientFilters.months || clientFilters.months.length === 0
          || clientFilters.months.includes(d.getMonth() + 1);
        return yearOk && monthOk;
      });
    }
    return opps;
  }, [opportunities, clientFilters, likedIds]);

  const safeScans = Array.isArray(scans) ? scans : [];
  const latestScan = safeScans.find(s => s.scan_state === 'completed_successfully') || null;

  const undatedEvents   = safeOpps.filter(o => !o.event_start_date);
  const noAddressEvents = safeOpps.filter(o => !o.address_city && !o.address_state && !o.address_raw);
  const noCoordsEvents  = safeOpps.filter(o => !o.latitude && !o.longitude);

  const subViewEvents = subView === 'undated'    ? undatedEvents
                      : subView === 'no-address' ? noAddressEvents
                      : subView === 'no-coords'  ? noCoordsEvents
                      : [];

  const subViewTitle  = subView === 'undated'    ? `Undated Events (${undatedEvents.length})`
                      : subView === 'no-address' ? `Events Without Address (${noAddressEvents.length})`
                      : subView === 'no-coords'  ? `Events Without Map Location (${noCoordsEvents.length})`
                      : '';

  const resetScan = (scanId) => {
    setSelectedScanIds([scanId]);
    setSubView(null);
    setApiFilters(DEFAULT_API_FILTERS);
    setClientFilters(DEFAULT_CLIENT_FILTERS);
  };

  return (
    <div className="app">

      <header className="header">
        <div className="header-inner">
          <div className="logo" style={{position:'relative'}}>
            <img
              src="/main_logo.jpeg"
              alt="Kayaking"
              className="logo-paddle logo-clickable"
              onClick={() => setAboutOpen(true)}
              title="About this app"
            />
            {aboutOpen && (
              <AboutModal
                onClose={() => setAboutOpen(false)}
                version={appVersion}
                latestScan={latestScan}
              />
            )}
          </div>
          <div className="logo-text">
            <div className="logo-title">KAYAK EDUCATION FINDER</div>
            <div className="logo-sub">USA, UK (incl. Wales), Mexico, Israel &amp; Canada · All Paddle Sports</div>
          </div>
          <div className="header-right">
            <div className="scan-picker-wrap">
              <button
                className="scan-selector"
                onClick={() => setScanPickerOpen(o => !o)}
              >
                {selectedScanIds.length === 0
                  ? 'No scan selected'
                  : selectedScanIds.length === 1
                    ? (() => { const s = safeScans.find(x => x.scan_id === selectedScanIds[0]); return s ? `${SCAN_STATE_LABEL[s.scan_state] || '?'} #${s.scan_id}${s.scan_name ? ' · ' + s.scan_name : ''} · ${safeFormat(s.end_timestamp || s.start_timestamp, 'MMM d, HH:mm')} · ${s.sites_found ?? 0} events` : '#' + selectedScanIds[0]; })()
                    : `${selectedScanIds.length} scans selected`
                } ▾
              </button>
              {scanPickerOpen && (
                <div className="scan-picker-dropdown">
                  {safeScans.map(s => (
                    <label key={s.scan_id} className="scan-picker-item">
                      <input
                        type="checkbox"
                        checked={selectedScanIds.includes(s.scan_id)}
                        onChange={e => {
                          setSelectedScanIds(prev =>
                            e.target.checked
                              ? [...prev, s.scan_id]
                              : prev.filter(id => id !== s.scan_id)
                          );
                        }}
                      />
                      <span className="scan-picker-label">
                        {SCAN_STATE_LABEL[s.scan_state] || '?'} #{s.scan_id}
                        {s.scan_name && <span className="scan-picker-name"> · {s.scan_name}</span>}
                        {s.is_default && ' ⭐'}
                        <span className="scan-picker-meta"> · {safeFormat(s.end_timestamp || s.start_timestamp, 'MMM d, HH:mm')} · {s.sites_found ?? 0} events</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="view-toggle">
              {['calendar', 'list', 'map'].map(v => (
                <button
                  key={v}
                  className={`view-btn ${view === v ? 'view-btn-active' : ''}`}
                  onClick={() => { setView(v); setSubView(null); }}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>


      <div className="app-body">
        <Filters
          apiFilters={apiFilters}
          setApiFilters={setApiFilters}
          clientFilters={clientFilters}
          setClientFilters={setClientFilters}
          totalEvents={safeOpps.length}
          undatedCount={undatedEvents.length}
          noAddressCount={noAddressEvents.length}
          subView={subView}
          onShowAll={() => setSubView(null)}
          onShowUndated={() => setSubView('undated')}
          onShowNoAddress={() => setSubView('no-address')}
          opportunities={safeOpps}
        />
        <main className="main-content">
          {scansLoading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading scans...</p>
            </div>
          ) : safeScans.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🌊</div>
              <h3>No scans yet</h3>
              <p>Run a scan to discover kayaking events.</p>
            </div>
          ) : loading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading events...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn-retry" onClick={fetchOpportunities}>Retry</button>
            </div>
          ) : subView && view === 'calendar' ? (
            <>
              <div className="cal-header-persistent">
                <div className="cal-month-title">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</div>
              </div>
              <SubListView
                events={subViewEvents}
                title={subViewTitle}
                onEventClick={setSelectedEvent}
                onBack={() => setSubView(null)}
                likedIds={likedIds}
                onToggleLike={toggleLike}
              />
            </>
          ) : subView ? (
            <SubListView
              events={subViewEvents}
              title={subViewTitle}
              onEventClick={setSelectedEvent}
              onBack={() => setSubView(null)}
              likedIds={likedIds}
              onToggleLike={toggleLike}
            />
          ) : view === 'calendar' ? (
            <Calendar
              opportunities={safeOpps}
              onEventClick={setSelectedEvent}
              likedIds={likedIds}
              onToggleLike={toggleLike}
              undatedCount={undatedEvents.length}
              onShowUndated={() => setSubView('undated')}
            />
          ) : view === 'map' ? (
            <MapView
              opportunities={safeOpps}
              onEventClick={setSelectedEvent}
              onNoCoords={() => setSubView('no-coords')}
              likedIds={likedIds}
              onToggleLike={toggleLike}
            />
          ) : (
            <ListView
              opportunities={safeOpps}
              onEventClick={setSelectedEvent}
              likedIds={likedIds}
              onToggleLike={toggleLike}
            />
          )}
        </main>
      </div>

      {selectedEvent && (
        <EventDetail
          opp={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          likedIds={likedIds}
          onToggleLike={toggleLike}
        />
      )}

    </div>
  );
}
